/**
 * Method Selector Component
 * Displays valuation method options with descriptions
 */
import React from 'react';
import PropTypes from 'prop-types';
import { Info, CheckCircle } from 'lucide-react';

const METHOD_DESCRIPTIONS = {
  cca: {
    name: "CCA (Comparable Company Analysis)",
    description: "Values your business by comparing it to similar companies using industry multiples EV/EBITDA and EV/Revenue ratios. Based on real market data from 95 industries covering thousands of public companies, with adjustable private company discount.",
    icon: "📊",
    pros: "Market-based, industry-specific, widely accepted, data from extensive public company analysis",
    cons: "Depends on quality of comparables",
    bestFor: "Established businesses in industries with good comparable data"
  },
  dcf: {
    name: "DCF (Discounted Cash Flow)",
    description: "Values your business using cash flow projections. Offers two calculation modes: (1) Multi-period DCF: Projects cash flows over a specified period (default 5 years) with terminal value, or (2) Gordon Growth Model: Perpetuity valuation assuming constant growth. Both use configurable discount rates (default 15%) and terminal growth rates (default 3%).",
    icon: "💰",
    pros: "Forward-looking, intrinsic value, considers growth, flexible calculation modes",
    cons: "Sensitive to assumptions, requires projections or growth estimates",
    bestFor: "High-growth businesses with predictable cash flows or stable businesses with consistent growth"
  },
  capitalization: {
    name: "Capitalization of Earnings",
    description: "Divides your annual earnings (EBITDA or net income) by a capitalization rate derived from industry multiples. Simple but effective for stable businesses.",
    icon: "💵",
    pros: "Simple, straightforward, based on current earnings",
    cons: "Assumes stable earnings, doesn't account for growth",
    bestFor: "Mature businesses with stable, predictable earnings"
  },
  nav: {
    name: "NAV (Net Asset Value)",
    description: "Calculates value as total assets minus total liabilities. Most appropriate for asset-heavy businesses or those in liquidation scenarios.",
    icon: "🏦",
    pros: "Objective, based on tangible assets, conservative",
    cons: "Ignores earnings potential and intangibles",
    bestFor: "Asset-heavy businesses, real estate, or liquidation scenarios"
  },
  manual: {
    name: "Manual Multiple",
    description: "Apply your own custom multiple to calculate valuation. Choose from EV/EBITDA or EV/Revenue ratios. Each selection displays the industry benchmark from our database of 95 industries to guide your analysis.",
    icon: "✏️",
    pros: "Flexible, allows custom analysis, can match specific comps, shows industry benchmarks",
    cons: "Requires knowledge of appropriate multiples",
    bestFor: "Experienced users with specific comparable data or target multiples"
  }
};

const MethodSelector = ({ selectedMethod, onMethodSelect, onShowDetails }) => {
  return (
    <div className="mb-6">
      <label className="block text-sm font-medium text-gray-700 mb-3">
        Valuation Method <span className="text-red-500">*</span>
      </label>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {Object.entries(METHOD_DESCRIPTIONS).map(([methodKey, method]) => (
          <button
            key={methodKey}
            onClick={() => onMethodSelect(methodKey)}
            className={`relative p-4 border-2 rounded-lg text-left transition-all hover:shadow-md ${
              selectedMethod === methodKey
                ? 'border-blue-500 bg-blue-50 shadow-md'
                : 'border-gray-200 hover:border-gray-300'
            }`}
          >
            {selectedMethod === methodKey && (
              <div className="absolute top-2 right-2">
                <CheckCircle className="text-blue-600" size={20} />
              </div>
            )}

            <div className="text-3xl mb-2">{method.icon}</div>
            <h3 className="font-semibold text-gray-900 mb-2 pr-6">
              {method.name}
            </h3>
            <p className="text-sm text-gray-600 mb-3 line-clamp-3">
              {method.description}
            </p>

            <div className="flex items-center justify-between mt-4">
              <span className="text-xs text-gray-500 font-medium">
                {method.bestFor.split(' ').slice(0, 4).join(' ')}...
              </span>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onShowDetails(methodKey);
                }}
                className="text-blue-600 hover:text-blue-700 p-1 rounded hover:bg-blue-50"
              >
                <Info size={16} />
              </button>
            </div>
          </button>
        ))}
      </div>

      {selectedMethod && (
        <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <h4 className="font-semibold text-blue-900 mb-2">
            {METHOD_DESCRIPTIONS[selectedMethod].icon} {METHOD_DESCRIPTIONS[selectedMethod].name}
          </h4>
          <div className="text-sm text-blue-800 space-y-2">
            <p><strong>Best for:</strong> {METHOD_DESCRIPTIONS[selectedMethod].bestFor}</p>
            <p><strong>Pros:</strong> {METHOD_DESCRIPTIONS[selectedMethod].pros}</p>
            <p><strong>Cons:</strong> {METHOD_DESCRIPTIONS[selectedMethod].cons}</p>
          </div>
        </div>
      )}
    </div>
  );
};

MethodSelector.propTypes = {
  selectedMethod: PropTypes.oneOf(['cca', 'dcf', 'capitalization', 'nav', 'manual']).isRequired,
  onMethodSelect: PropTypes.func.isRequired,
  onShowDetails: PropTypes.func.isRequired
};

export { METHOD_DESCRIPTIONS };
export default MethodSelector;
